import React from "react";
import { connect } from "react-redux";
import withStyles from "@material-ui/core/styles/withStyles";
import Dialog from "@material-ui/core/Dialog";
import Chip from "@material-ui/core/Chip";
import Switch from "@material-ui/core/Switch";
import Collapse from "@material-ui/core/Collapse";
import { searchMedia, suggestMedia, getMediaDownloadUrl } from "./media-library-api.js";

// Browse + search saved Media Library assets (old CMS). Search-first, filter-rich
// look to match the new CMS — media-type + source facets (data-driven with counts),
// editorial / date / sort refinements, active-filter chips, and a reuse-first
// detail dialog. Reads through the absinthe /media proxy the upload flow uses;
// refreshes when `refreshKey` changes. React 16 + MUI v4, no new deps.

const ACCENT = "#e91e63";
const LIMIT = 30;
const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "relevance", label: "Most relevant" },
  { value: "largest", label: "Largest" },
  { value: "longest", label: "Longest" }
];
const TYPES = [
  { value: "", label: "All" },
  { value: "image", label: "Images" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" }
];

const styles = {
  // search
  searchWrap: { position: "relative", maxWidth: 560, margin: "4px 0 16px" },
  searchIcon: {
    position: "absolute", left: 15, top: "50%", marginTop: -9,
    width: 18, height: 18, color: "#9ca3af", pointerEvents: "none"
  },
  searchInput: {
    width: "100%", height: 46, padding: "0 40px 0 44px", fontSize: 15,
    border: "1px solid #e5e7eb", borderRadius: 12, outline: "none",
    color: "#111827", background: "#fff", boxSizing: "border-box",
    transition: "border-color .15s, box-shadow .15s",
    "&::placeholder": { color: "#9ca3af" },
    "&:focus": { borderColor: ACCENT, boxShadow: "0 0 0 3px rgba(233,30,99,0.12)" }
  },
  searchClear: {
    position: "absolute", right: 12, top: "50%", marginTop: -10, width: 20, height: 20,
    border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center"
  },
  // suggest dropdown
  suggest: {
    position: "absolute", left: 0, right: 0, top: "calc(100% + 6px)", zIndex: 30,
    background: "#fff", border: "1px solid #eceef1", borderRadius: 12,
    boxShadow: "0 12px 32px rgba(17,24,39,0.14)", padding: "6px 0", overflow: "hidden"
  },
  suggestHead: {
    padding: "6px 14px 4px", fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
    textTransform: "uppercase", color: "#9ca3af"
  },
  suggestItem: {
    display: "flex", alignItems: "center", gap: 8, width: "100%", border: "none",
    background: "none", padding: "7px 14px", fontSize: 14, color: "#1f2937",
    cursor: "pointer", textAlign: "left",
    "&:hover": { background: "#f8f9fb" }
  },
  suggestTags: { display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 14px 8px" },
  suggestTag: {
    border: "1px solid #e5e7eb", background: "#f8f9fb", borderRadius: 999,
    padding: "3px 10px", fontSize: 12, color: "#4b5563", cursor: "pointer",
    "&:hover": { borderColor: ACCENT, background: "#fff" }
  },
  // filter row
  filterRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 },
  seg: { display: "inline-flex", alignItems: "center", border: "1px solid #e5e7eb", background: "#f6f7f9", borderRadius: 10, padding: 3 },
  segBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "none",
    borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 600, color: "#6b7280",
    cursor: "pointer", transition: "background .15s, color .15s",
    "&:hover": { color: "#111827" }
  },
  segBtnActive: { background: "#fff", color: "#111827", boxShadow: "0 1px 2px rgba(17,24,39,0.10)" },
  segBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  segCount: { fontSize: 12, color: "#9ca3af", fontVariantNumeric: "tabular-nums" },
  select: {
    height: 38, minWidth: 130, fontSize: 13, background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 9, padding: "0 10px"
  },
  moreBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, height: 38, padding: "0 14px",
    fontSize: 13, fontWeight: 600, color: "#374151", background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 9, cursor: "pointer",
    "&:hover": { background: "#f9fafb", borderColor: "#d1d5db" }
  },
  moreBadge: {
    minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: ACCENT,
    color: "#fff", fontSize: 11, fontWeight: 700, display: "inline-flex",
    alignItems: "center", justifyContent: "center"
  },
  spacer: { marginLeft: "auto" },
  density: { display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 9, padding: 3, background: "#fff" },
  densityBtn: {
    width: 30, height: 30, border: "none", background: "none", borderRadius: 7, cursor: "pointer",
    color: "#9ca3af", display: "inline-flex", alignItems: "center", justifyContent: "center"
  },
  densityBtnActive: { background: "#f1f3f5", color: "#111827" },
  // more panel
  morePanel: {
    border: "1px solid #eceef1", borderRadius: 12, padding: 16, marginBottom: 12,
    display: "flex", flexWrap: "wrap", gap: 28, alignItems: "flex-start", background: "#fcfcfd"
  },
  moreGroup: { minWidth: 200 },
  moreLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#6b7280", marginBottom: 8 },
  presets: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 },
  preset: {
    border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "4px 10px",
    fontSize: 12, color: "#374151", cursor: "pointer", "&:hover": { borderColor: "#d1d5db" }
  },
  dateInputs: { display: "flex", alignItems: "center", gap: 8 },
  dateInput: {
    height: 34, border: "1px solid #e5e7eb", borderRadius: 8, padding: "0 8px", fontSize: 13, color: "#374151"
  },
  // chips
  chipRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 16, minHeight: 24 },
  clearAll: { border: "none", background: "none", color: "#6b7280", fontSize: 13, cursor: "pointer", padding: "2px 6px" },
  count: { marginLeft: "auto", fontSize: 13, color: "#9ca3af", fontVariantNumeric: "tabular-nums" },
  // grid
  grid: { display: "grid", gap: 16 },
  card: {
    border: "1px solid #eceef1", borderRadius: 12, overflow: "hidden", background: "#fff", cursor: "pointer",
    transition: "box-shadow .18s ease, transform .18s ease, border-color .18s",
    "&:hover": { boxShadow: "0 8px 24px rgba(17,24,39,0.10)", borderColor: "#dfe3e8", transform: "translateY(-2px)" }
  },
  thumbWrap: { position: "relative", paddingTop: "100%", background: "#f3f4f6" },
  thumb: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" },
  thumbEmpty: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center",
    justifyContent: "center", color: "#c4c9d0", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5
  },
  badge: {
    position: "absolute", top: 9, left: 9, background: "rgba(17,24,39,0.72)", color: "#fff", fontSize: 10,
    fontWeight: 600, textTransform: "capitalize", padding: "3px 8px", borderRadius: 999, letterSpacing: 0.3
  },
  body: { padding: "11px 13px 13px" },
  title: { fontSize: 13, fontWeight: 600, color: "#1f2937", lineHeight: 1.35, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  sub: { fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 3 },
  skel: { borderRadius: 12, paddingTop: "100%", background: "linear-gradient(90deg,#f3f4f6,#eceef1,#f3f4f6)" },
  state: { padding: "56px 0", textAlign: "center", color: "#9ca3af", fontSize: 14 },
  stateBtn: {
    marginTop: 12, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "6px 14px",
    fontSize: 13, color: "#374151", cursor: "pointer"
  },
  pager: { display: "flex", justifyContent: "center", alignItems: "center", gap: 14, margin: "8px 0 28px" },
  pageBtn: {
    height: 34, padding: "0 16px", fontSize: 13, fontWeight: 600, color: "#374151", background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer",
    "&:hover:not(:disabled)": { background: "#f9fafb", borderColor: "#d1d5db" },
    "&:disabled": { opacity: 0.45, cursor: "default" }
  },
  pageInfo: { fontSize: 13, color: "#9ca3af" },
  // detail modal
  modalPaper: { maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: 14, overflow: "hidden" },
  modalScroll: { overflowY: "auto", overflowX: "hidden", flex: "1 1 auto", minHeight: 0, padding: "22px 24px 24px" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#111827", margin: "0 0 12px" },
  modalBadges: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 },
  modalBadge: { fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: "#f1f3f5", color: "#4b5563", textTransform: "capitalize" },
  modalBadgeRed: { background: "#fdecef", color: "#c2185b" },
  modalImgWrap: { textAlign: "center", background: "#f3f4f6", borderRadius: 10, padding: 10, marginBottom: 20 },
  modalImg: { maxWidth: "100%", maxHeight: "56vh", objectFit: "contain", borderRadius: 4 },
  metaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px 24px" },
  metaLabel: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: "#9ca3af", marginBottom: 3 },
  metaVal: { fontSize: 14, color: "#374151", lineHeight: 1.4 },
  keyword: {
    display: "inline-block", fontSize: 11, color: "#4b5563", background: "#f3f4f6", borderRadius: 999,
    padding: "3px 10px", margin: "0 6px 6px 0", cursor: "pointer", border: "1px solid transparent",
    "&:hover": { borderColor: ACCENT, background: "#fff" }
  },
  modalFoot: {
    display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", borderTop: "1px solid #f0f1f3", flex: "0 0 auto"
  },
  navBtn: {
    width: 34, height: 34, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer",
    color: "#374151", display: "inline-flex", alignItems: "center", justifyContent: "center",
    "&:disabled": { opacity: 0.4, cursor: "default" }
  },
  ghostBtn: {
    height: 36, padding: "0 14px", fontSize: 13, fontWeight: 600, color: "#374151", background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", display: "inline-flex",
    alignItems: "center", gap: 6, textDecoration: "none"
  },
  closeBtn: {
    height: 36, padding: "0 20px", fontSize: 13, fontWeight: 600, color: "#fff", background: ACCENT,
    border: "none", borderRadius: 8, cursor: "pointer"
  }
};

const norm = function(u) { return (u && u.indexOf("//") === 0 ? "https:" + u : u) || undefined; };
const thumbOf = function(a) { return norm(a.thumbUrl) || norm(a.posterUrl) || norm(a.imageUrl) || norm(a.url); };
const bigOf = function(a) { return norm(a.webUrl) || norm(a.imageUrl) || norm(a.url) || norm(a.posterUrl) || norm(a.thumbUrl); };
const origOf = function(a) { return norm(a.imageUrl) || norm(a.url); };
const iso = function(d) {
  var p = function(n) { return (n < 10 ? "0" : "") + n; };
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
};
// Upload timestamp, matching the new CMS format ("28/07/2026, 00:39:23").
const fmtDateTime = function(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString("en-GB"); } catch (e) { return String(v); }
};

const SearchIcon = function(cls) {
  return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
};

class MediaLibraryGrid extends React.Component {
  constructor(props) {
    super(props);
    this.state = Object.assign({
      items: [], facets: null, loading: false, page: 1, total: 0, selected: null,
      showMore: false, density: "comfortable",
      suggest: { titles: [], keywords: [] }, suggestOpen: false, copied: false, downloading: false
    }, this.parseUrl());
    this._timer = null;
    this._sugTimer = null;
    this._blurTimer = null;
    this._unmounted = false;
  }

  componentDidMount() { this.fetch(1); }
  componentDidUpdate(prev) { if (prev.refreshKey !== this.props.refreshKey) this.fetch(1); }
  componentWillUnmount() {
    this._unmounted = true;
    clearTimeout(this._timer); clearTimeout(this._sugTimer); clearTimeout(this._blurTimer);
  }

  // ---- URL state (replaceState only — never triggers react-router) ----
  parseUrl() {
    var out = { q: "", mediaType: "", source: "", editorial: "", from: "", to: "", keywords: [], sort: "" };
    try {
      var sp = new URLSearchParams(window.location.search);
      out.q = sp.get("mq") || "";
      out.mediaType = sp.get("mtype") || "";
      out.source = sp.get("msource") || "";
      out.editorial = sp.get("meditorial") || "";
      out.from = sp.get("mfrom") || "";
      out.to = sp.get("mto") || "";
      out.sort = sp.get("msort") || "";
      var kw = sp.get("mkw");
      out.keywords = kw ? kw.split(",").filter(Boolean) : [];
    } catch (e) { /* SSR / old browser — ignore */ }
    return out;
  }
  syncUrl() {
    try {
      var s = this.state;
      var sp = new URLSearchParams(window.location.search);
      var setOrDel = function(key, val) { if (val) sp.set(key, val); else sp.delete(key); };
      setOrDel("mq", s.q);
      setOrDel("mtype", s.mediaType);
      setOrDel("msource", s.source);
      setOrDel("meditorial", s.editorial);
      setOrDel("mfrom", s.from);
      setOrDel("mto", s.to);
      setOrDel("msort", s.sort);
      setOrDel("mkw", s.keywords.join(","));
      var qs = sp.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : "") + window.location.hash);
    } catch (e) { /* ignore */ }
  }

  // ---- data ----
  fetch(page) {
    var self = this;
    var p = page || this.state.page;
    var s = this.state;
    this.setState({ loading: true });
    searchMedia(this.props.activeDomain, {
      q: s.q, mediaType: s.mediaType, source: s.source, editorial: s.editorial,
      from: s.from, to: s.to, keywords: s.keywords, sort: s.sort, page: p, limit: LIMIT
    }).then(function(res) {
      if (self._unmounted) return;
      self.setState({
        items: res.items || [],
        facets: res.facets || null,
        total: (res.pagination && res.pagination.total) || 0,
        page: p, loading: false
      }, function() { self.syncUrl(); });
    }).catch(function() { if (!self._unmounted) self.setState({ loading: false }); });
  }

  // Merge a filter change, reset to page 1, refetch.
  update(patch) {
    var self = this;
    this.setState(Object.assign({ page: 1 }, patch), function() { self.fetch(1); });
  }

  onSearch(e) {
    var v = e.target.value;
    var self = this;
    this.setState({ q: v });
    clearTimeout(this._timer);
    this._timer = setTimeout(function() { self.fetch(1); }, 350);
    clearTimeout(this._sugTimer);
    this._sugTimer = setTimeout(function() { self.fetchSuggest(v); }, 180);
  }
  fetchSuggest(q) {
    var self = this;
    suggestMedia(this.props.activeDomain, q).then(function(r) {
      if (!self._unmounted) self.setState({ suggest: r || { titles: [], keywords: [] } });
    }).catch(function() {});
  }
  pickTitle(t) {
    var self = this;
    this.setState({ q: t, suggestOpen: false }, function() { self.fetch(1); });
  }
  addKeyword(k) {
    if (this.state.keywords.indexOf(k) >= 0) return;
    this.update({ keywords: this.state.keywords.concat([k]), suggestOpen: false });
  }
  removeKeyword(k) {
    this.update({ keywords: this.state.keywords.filter(function(x) { return x !== k; }) });
  }
  clearAll() {
    this.update({ q: "", mediaType: "", source: "", editorial: "", from: "", to: "", keywords: [], sort: "" });
  }
  setDensity(d) { this.setState({ density: d }); }
  preset(kind) {
    var to = new Date();
    var from = new Date();
    if (kind === "year") from.setMonth(0, 1);
    else from.setDate(from.getDate() - kind);
    this.update({ from: iso(from), to: iso(to) });
  }

  activeCount() {
    var s = this.state;
    return (s.q ? 1 : 0) + (s.mediaType ? 1 : 0) + (s.source ? 1 : 0) + (s.editorial ? 1 : 0) +
      (s.from || s.to ? 1 : 0) + s.keywords.length + (s.sort ? 1 : 0);
  }
  moreCount() {
    var s = this.state;
    return (s.editorial ? 1 : 0) + (s.from || s.to ? 1 : 0);
  }
  typeCounts() {
    var m = {};
    var arr = (this.state.facets && this.state.facets.media_type) || [];
    for (var i = 0; i < arr.length; i++) m[arr[i].value] = arr[i].count;
    return m;
  }

  renderChips() {
    var c = this.props.classes;
    var s = this.state;
    var self = this;
    var chips = [];
    if (s.q) chips.push({ key: "q", label: '“' + s.q + '”', on: function() { self.update({ q: "" }); } });
    if (s.mediaType) {
      var tl = { image: "Images", video: "Video", audio: "Audio" }[s.mediaType] || s.mediaType;
      chips.push({ key: "mt", label: tl, on: function() { self.update({ mediaType: "" }); } });
    }
    if (s.source) chips.push({ key: "src", label: "Source: " + s.source, on: function() { self.update({ source: "" }); } });
    if (s.editorial) chips.push({ key: "ed", label: "Editorial only", on: function() { self.update({ editorial: "" }); } });
    if (s.from || s.to) chips.push({ key: "dt", label: (s.from || "…") + " → " + (s.to || "…"), on: function() { self.update({ from: "", to: "" }); } });
    if (s.sort) {
      var sl = (SORTS.filter(function(x) { return x.value === s.sort; })[0] || {}).label || s.sort;
      chips.push({ key: "sort", label: "Sort: " + sl, on: function() { self.update({ sort: "" }); } });
    }
    s.keywords.forEach(function(k) { chips.push({ key: "kw-" + k, label: "#" + k, on: function() { self.removeKeyword(k); } }); });

    if (!chips.length) {
      return (
        <div className={c.chipRow}>
          {!s.loading ? <span className={c.count}>{s.total} assets</span> : null}
        </div>
      );
    }
    return (
      <div className={c.chipRow}>
        {chips.map(function(ch) {
          return <Chip key={ch.key} label={ch.label} onDelete={ch.on} size="small" style={{ background: "#f1f3f5" }} />;
        })}
        <button className={c.clearAll} onClick={function() { self.clearAll(); }}>Clear all</button>
        {!s.loading ? <span className={c.count}>{s.total} assets</span> : null}
      </div>
    );
  }

  renderDetail() {
    var a = this.state.selected;
    if (!a) return null;
    var c = this.props.classes;
    var self = this;
    var items = this.state.items;
    var idx = items.map(function(x) { return x.publicId; }).indexOf(a.publicId);
    var url = bigOf(a);
    var orig = origOf(a);
    var copyTarget = orig || url;
    var close = function() { self.setState({ selected: null, copied: false }); };
    var go = function(delta) {
      var n = items[idx + delta];
      if (n) self.setState({ selected: n, copied: false });
    };
    var copy = function() {
      if (copyTarget && navigator.clipboard) {
        navigator.clipboard.writeText(copyTarget).then(function() {
          self.setState({ copied: true });
          setTimeout(function() { if (!self._unmounted) self.setState({ copied: false }); }, 1500);
        });
      }
    };
    // Download the ORIGINAL: the backend mints a presigned R2 GET with
    // Content-Disposition: attachment (forces a real download cross-origin).
    var download = function() {
      if (self.state.downloading) return;
      self.setState({ downloading: true });
      getMediaDownloadUrl(self.props.activeDomain, a.publicId).then(function(res) {
        var el = document.createElement("a");
        el.href = res.downloadUrl;
        el.rel = "noopener";
        document.body.appendChild(el);
        el.click();
        el.remove();
        if (!self._unmounted) self.setState({ downloading: false });
      }).catch(function() { if (!self._unmounted) self.setState({ downloading: false }); });
    };
    var lic = a.licenseModel ? String(a.licenseModel).replace(/_/g, " ") : "";
    // Show the uploader as a person (name + avatar) — the backend resolves the id
    // against video_users AND the old-CMS users table into `uploadedByUser`. Fall
    // back to a labelled id only if nothing resolved.
    var uUser = a.uploadedByUser;
    var uName = (uUser && uUser.name) ? uUser.name : (a.uploadedBy ? "User " + a.uploadedBy : "");
    var uAvatar = uUser && (uUser.profilePicture || uUser.profilePictureUrl || uUser.avatar);
    var uploaderNode = uName ? (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {uAvatar ? <img src={norm(uAvatar)} alt="" style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover", marginRight: 6 }} /> : null}
        <span>{uName}</span>
      </span>
    ) : "";
    var rows = [
      ["Credit line", a.creditLine], ["Copyright", a.copyrightNotice], ["Photographer", a.photographer],
      ["Source", a.source], ["License", lic], ["Location", [a.city, a.country].filter(Boolean).join(", ")],
      ["Description", a.description], ["Uploaded by", uploaderNode], ["Uploaded", fmtDateTime(a.createdAt)],
      ["Dimensions", a.width && a.height ? a.width + " × " + a.height : ""]
    ].filter(function(r) { return r[1]; });

    return (
      <Dialog open onClose={close} maxWidth="md" fullWidth scroll="paper" PaperProps={{ className: c.modalPaper }}>
        <div className={c.modalScroll}>
          <h3 className={c.modalTitle}>{a.title || "Untitled"}</h3>
          <div className={c.modalBadges}>
            <span className={c.modalBadge}>{a.mediaType || "image"}</span>
            {a.source ? <span className={c.modalBadge}>{a.source}</span> : null}
            {a.editorialOnly ? <span className={c.modalBadge + " " + c.modalBadgeRed}>Editorial only</span> : null}
            {lic ? <span className={c.modalBadge}>{lic}</span> : null}
          </div>
          {url ? <div className={c.modalImgWrap}><img src={url} alt={a.title || ""} className={c.modalImg} /></div> : null}
          <div className={c.metaGrid}>
            {rows.map(function(r) {
              return (
                <div key={r[0]} style={r[0] === "Description" ? { gridColumn: "1 / -1" } : null}>
                  <div className={c.metaLabel}>{r[0]}</div>
                  <div className={c.metaVal}>{r[1]}</div>
                </div>
              );
            })}
          </div>
          {Array.isArray(a.keywords) && a.keywords.length ? (
            <div style={{ marginTop: 16 }}>
              <div className={c.metaLabel}>Keywords</div>
              <div style={{ marginTop: 6 }}>
                {a.keywords.map(function(k) {
                  return <span key={k} className={c.keyword} onClick={function() { self.setState({ selected: null }); self.addKeyword(k); }}>{k}</span>;
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div className={c.modalFoot}>
          <button className={c.navBtn} disabled={idx <= 0} onClick={function() { go(-1); }} aria-label="Previous">&#8249;</button>
          <button className={c.navBtn} disabled={idx < 0 || idx >= items.length - 1} onClick={function() { go(1); }} aria-label="Next">&#8250;</button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
            {copyTarget ? <button className={c.ghostBtn} onClick={copy}>{self.state.copied ? "Copied" : "Copy URL"}</button> : null}
            {orig ? <a className={c.ghostBtn} href={orig} target="_blank" rel="noopener noreferrer">Open original ↗</a> : null}
            <button className={c.ghostBtn} disabled={self.state.downloading} onClick={download}>{self.state.downloading ? "Preparing…" : "Download ↓"}</button>
            <button className={c.closeBtn} onClick={close}>Close</button>
          </div>
        </div>
      </Dialog>
    );
  }

  render() {
    var c = this.props.classes;
    var s = this.state;
    var self = this;
    var totalPages = Math.max(1, Math.ceil(s.total / LIMIT));
    var sug = s.suggest;
    var showSug = s.suggestOpen && (s.q || "").trim().length >= 2 && ((sug.titles && sug.titles.length) || (sug.keywords && sug.keywords.length));
    var typeCounts = this.typeCounts();
    var totalTypeCount = ((s.facets && s.facets.media_type) || []).reduce(function(x, b) { return x + b.count; }, 0);
    var sources = ((s.facets && s.facets.source) || []).filter(function(b) { return b.count > 0; });
    var moreCount = this.moreCount();
    var minTile = s.density === "compact" ? 128 : 168;
    var gridStyle = { gridTemplateColumns: "repeat(auto-fill, minmax(" + minTile + "px, 1fr))" };

    return (
      <div style={{ marginTop: 4 }}>
        {/* Search + suggest */}
        <div className={c.searchWrap}>
          {SearchIcon(c.searchIcon)}
          <input
            className={c.searchInput}
            type="text"
            placeholder="Search title, description, keywords…"
            value={s.q}
            onChange={function(e) { self.onSearch(e); }}
            onFocus={function() { self.setState({ suggestOpen: true }); }}
            onBlur={function() { self._blurTimer = setTimeout(function() { if (!self._unmounted) self.setState({ suggestOpen: false }); }, 140); }}
            onKeyDown={function(e) { if (e.key === "Enter") { clearTimeout(self._timer); self.fetch(1); self.setState({ suggestOpen: false }); } }}
          />
          {s.q ? (
            <button className={c.searchClear} onMouseDown={function(e) { e.preventDefault(); }} onClick={function() { self.update({ q: "" }); }} aria-label="Clear search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          ) : null}
          {showSug ? (
            <div className={c.suggest} onMouseDown={function(e) { e.preventDefault(); }}>
              {sug.titles && sug.titles.length ? (
                <div>
                  <div className={c.suggestHead}>Titles</div>
                  {sug.titles.slice(0, 6).map(function(t) {
                    return <button key={t} className={c.suggestItem} onClick={function() { self.pickTitle(t); }}>{SearchIcon(null)}<span>{t}</span></button>;
                  })}
                </div>
              ) : null}
              {sug.keywords && sug.keywords.length ? (
                <div>
                  <div className={c.suggestHead}>Tags</div>
                  <div className={c.suggestTags}>
                    {sug.keywords.slice(0, 8).map(function(k) {
                      return <span key={k} className={c.suggestTag} onClick={function() { self.addKeyword(k); }}>{k}</span>;
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Filter row */}
        <div className={c.filterRow}>
          <div className={c.seg}>
            {TYPES.map(function(t) {
              var active = s.mediaType === t.value;
              var count = t.value === "" ? totalTypeCount : (typeCounts[t.value] || 0);
              var disabled = t.value !== "" && !active && count === 0;
              var cls = c.segBtn + (active ? " " + c.segBtnActive : "") + (disabled ? " " + c.segBtnDisabled : "");
              return (
                <button key={t.value || "all"} className={cls} disabled={disabled} onClick={function() { self.update({ mediaType: t.value }); }}>
                  {t.label}
                  {s.facets && count > 0 ? <span className={c.segCount}>{count}</span> : null}
                </button>
              );
            })}
          </div>

          <select
            value={s.source}
            onChange={function(e) { self.update({ source: e.target.value }); }}
            className={c.select}
          >
            <option value="">All sources</option>
            {sources.map(function(b) {
              return <option key={b.value} value={b.value}>{b.value} ({b.count})</option>;
            })}
          </select>

          <button className={c.moreBtn} onClick={function() { self.setState({ showMore: !s.showMore }); }}>
            More {moreCount ? <span className={c.moreBadge}>{moreCount}</span> : null}
          </button>

          <div className={c.spacer} />

          <select
            value={s.sort}
            onChange={function(e) { self.update({ sort: e.target.value }); }}
            className={c.select}
            style={{ minWidth: 120 }}
          >
            <option value="">Sort</option>
            {SORTS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>

          <div className={c.density}>
            <button className={c.densityBtn + (s.density === "comfortable" ? " " + c.densityBtnActive : "")} onClick={function() { self.setDensity("comfortable"); }} title="Comfortable" aria-label="Comfortable">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="8" rx="1.5"/><rect x="3" y="13" width="8" height="8" rx="1.5"/><rect x="13" y="13" width="8" height="8" rx="1.5"/></svg>
            </button>
            <button className={c.densityBtn + (s.density === "compact" ? " " + c.densityBtnActive : "")} onClick={function() { self.setDensity("compact"); }} title="Compact" aria-label="Compact">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="10" y="3" width="5" height="5" rx="1"/><rect x="17" y="3" width="4" height="5" rx="1"/><rect x="3" y="10" width="5" height="5" rx="1"/><rect x="10" y="10" width="5" height="5" rx="1"/><rect x="17" y="10" width="4" height="5" rx="1"/><rect x="3" y="17" width="5" height="4" rx="1"/><rect x="10" y="17" width="5" height="4" rx="1"/><rect x="17" y="17" width="4" height="4" rx="1"/></svg>
            </button>
          </div>
        </div>

        {/* More panel */}
        <Collapse in={s.showMore}>
          <div className={c.morePanel}>
            <div className={c.moreGroup}>
              <div className={c.moreLabel}>Rights</div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14, color: "#374151" }}>
                <Switch checked={s.editorial === "true"} onChange={function(e) { self.update({ editorial: e.target.checked ? "true" : "" }); }} color="primary" size="small" />
                Editorial only
              </label>
            </div>
            <div className={c.moreGroup}>
              <div className={c.moreLabel}>Date added</div>
              <div className={c.presets}>
                <button className={c.preset} onClick={function() { self.preset(7); }}>7 days</button>
                <button className={c.preset} onClick={function() { self.preset(30); }}>30 days</button>
                <button className={c.preset} onClick={function() { self.preset("year"); }}>This year</button>
                {(s.from || s.to) ? <button className={c.preset} onClick={function() { self.update({ from: "", to: "" }); }}>Clear</button> : null}
              </div>
              <div className={c.dateInputs}>
                <input type="date" className={c.dateInput} value={s.from} onChange={function(e) { self.update({ from: e.target.value }); }} />
                <span style={{ color: "#9ca3af" }}>&rarr;</span>
                <input type="date" className={c.dateInput} value={s.to} onChange={function(e) { self.update({ to: e.target.value }); }} />
              </div>
            </div>
          </div>
        </Collapse>

        {/* Chips + count */}
        {this.renderChips()}

        {/* Grid */}
        {s.loading && !s.items.length ? (
          <div className={c.grid} style={gridStyle}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map(function(i) { return <div key={i} className={c.skel} />; })}
          </div>
        ) : !s.items.length ? (
          <div className={c.state}>
            {self.activeCount() ? (
              <div>
                <div>No assets match these filters.</div>
                <button className={c.stateBtn} onClick={function() { self.clearAll(); }}>Clear all filters</button>
              </div>
            ) : "No media yet — upload something above."}
          </div>
        ) : (
          <div className={c.grid} style={gridStyle}>
            {s.items.map(function(a) {
              var t = thumbOf(a);
              return (
                <div key={a.publicId} className={c.card} onClick={function() { self.setState({ selected: a }); }}>
                  <div className={c.thumbWrap}>
                    {t ? <img src={t} alt={a.title || ""} className={c.thumb} /> : <div className={c.thumbEmpty}>{a.mediaType || "image"}</div>}
                    {a.mediaType && a.mediaType !== "image" ? <span className={c.badge}>{a.mediaType}</span> : null}
                  </div>
                  {s.density === "comfortable" ? (
                    <div className={c.body}>
                      <div className={c.title} title={a.title || ""}>{a.title || "Untitled"}</div>
                      {a.source ? <div className={c.sub}>{a.source}</div> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {/* Pager */}
        {totalPages > 1 ? (
          <div className={c.pager}>
            <button className={c.pageBtn} disabled={s.page <= 1} onClick={function() { self.fetch(s.page - 1); }}>Prev</button>
            <span className={c.pageInfo}>Page {s.page} of {totalPages} · {s.total}</span>
            <button className={c.pageBtn} disabled={s.page >= totalPages} onClick={function() { self.fetch(s.page + 1); }}>Next</button>
          </div>
        ) : null}

        {this.renderDetail()}
      </div>
    );
  }
}

const mapStateToProps = function(store) { return { activeDomain: store.login.user.activeDomain }; };

export default connect(mapStateToProps)(withStyles(styles)(MediaLibraryGrid));
